import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { ADTColumns } from 'angular-datatables/src/models/settings';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import {
	AgrupamentoEspecial,
	DadosAgrupamento,
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	PeriodosLabel,
} from '../../../interfaces';
import { RelatorioNDCResponse } from '../../../interfaces/relatorio-ndc';
import { RelatoriosService } from '../../../services';
import { corrigeDuplicacaoNome } from '../../../utils';
import { DateTimeUtils } from '@utils/datetime-util';
import {
	enumAsSelectOptions,
	isNuloOuUndefined,
	numberToBrNumber,
	renderCoordenada,
	setAgrupamentoEspecial,
} from '@utils';

import { PublicTableComponent } from '@componentes/public-table/public-table.component';
import {
	exportarCSV,
	exportarPDF,
	exportarTXT,
	exportarXLSX,
} from '@home/submodulos/dados-meteorologicos/utils/exportacao-tabelas';
import { configsColunasCoordenadas } from '../../../utils/configs-colunas-exportacao';

@Component({
	selector: 'seira-tabela-ndc',
	templateUrl: './tabela-ndc.component.html',
	styleUrls: ['./tabela-ndc.component.scss'],
})
export class TabelaNdcComponent implements OnInit, OnDestroy {
	inputs = inject(INPUTS_RELATORIOS);
	_destroyed = new Subject();

	tituloTabela = 'Número de dias com chuva (NDC)';
	tituloColunaAgrupamento = 'Agrupamento';
	descricaoRelatorio =
		'O NDC, Número de dias com chuva, representa o número de dias em que a pluviometria diária, acumulada em 24 horas, foi igual ou superior a 0,1 mm.';

	carregando = true;
	disabledSelectList = false;

	colunasNDC: ADTColumns[] = [];
	resultados: any[] = [];
	colunasSelecionadas: string[] = [];
	periodoAgrupamento = '';
	agrupamento: any[];

	@ViewChild('tabelaNDC', { static: false })
	tabela?: PublicTableComponent;
	opcoesColunas = [
		{ name: 'Município/Posto', isFixed: true },
		{ name: 'Latitude', isFixed: false },
		{ name: 'Longitude', isFixed: false },
		{ name: 'Altitude', isFixed: false },
		{ name: 'Município', isFixed: false },
		{ name: 'Microrregião', isFixed: false },
		{ name: 'Mesorregião', isFixed: false },
		{ name: 'Região Pluviométrica', isFixed: false },
		{ name: 'Bacia/Sub-bacia', isFixed: false },
	];
	botoesDeExportacao: GroupButton[] = [
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () =>
				exportarPDF({
					resultados: this.resultados,
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
					colunasAEsquerda:
						this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value ===
						'MUNICIPIO_POSTO'
							? [0]
							: [],
				}),
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () =>
				exportarCSV({
					tabela: this.tabela!,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
				}),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () =>
				exportarTXT({
					tabela: this.tabela!,
					periodoAgrupamento: this.periodoAgrupamento,
					titulo: this.tituloTabela,
				}),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () =>
				exportarXLSX({
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
					configsColunas: [
						...configsColunasCoordenadas,
						{
							coluna: 'somaPrecipitacao',
							casasDecimais: 1,
						},
					],
					colunasAEsquerda:
						this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value ===
						'MUNICIPIO_POSTO'
							? [0]
							: [],
				}),
		},
	];

	constructor(
		private relatoriosService: RelatoriosService,
		private toastrService: ToastrService
	) {
		this.agrupamento = enumAsSelectOptions(AgrupamentoEspecial);
		this.colunasSelecionadas = this.opcoesColunas
			.filter(option => option.isFixed)
			.map(option => option.name);
	}

	ngOnInit() {
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValue(this.agrupamento[0].value);
		this.setValidators();
	}

	ngOnDestroy() {
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.clearValidators();
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValidators(Validators.required);
	}

	getDataFromForm(data: FormularioRelatorio) {
		return this.inputs.form.get(data);
	}

	setColunasSelecionadas(colunas: any) {
		this.colunasSelecionadas = colunas;
		this.gerarRelatorio();
	}

	gerarRelatorio() {
		const diaInicio = this.getDataFromForm(FormularioRelatorio.DATA_INICIO)
			?.value;
		const diaFim = this.getDataFromForm(FormularioRelatorio.DATA_FIM)?.value;
		const agrupamento = this.getDataFromForm(FormularioRelatorio.AGRUPAMENTO)
			?.value;
		this.periodoAgrupamento = this.getDataFromForm(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value as PeriodosLabel;

		if (
			!diaInicio ||
			!diaFim ||
			!agrupamento ||
			!this.periodoAgrupamento ||
			this.inputs.form.invalid
		) {
			return;
		}

		this.tituloTabela = `Número de dias com chuva (NDC) - ${DateTimeUtils.formatarDataPeriodo(
			diaInicio,
			diaFim,
			this.periodoAgrupamento
		)}`;
		this.carregando = true;
		this.inputs.setLoading(true);

		this.colunasNDC = [];
		this.resultados = [];

		const dados: DadosAgrupamento = {
			diaInicio,
			diaFim,
			agrupamento,
			periodoAgrupamento: this.periodoAgrupamento.toUpperCase(),
			isRetornoAgrupado: true,
		};

		switch (agrupamento) {
			case 'MUNICIPIO_POSTO':
				dados.posto = 0;
				break;
			case 'MICRORREGIAO':
				dados.microrregiao = 0;
				break;
			case 'MUNICIPIO':
				dados.municipio = 0;
				break;
		}

		this.relatoriosService.buscarRelatorioNDCPorAgrupamento(dados).subscribe({
			next: ndcAgrupados => {
				const agrupamento = this.inputs.form.get('agrupamento')?.value;
				this.relatoriosService.atualizaVisibilidadeBotoesExportacao(
					ndcAgrupados
				);
				this.resultados = this.padronizarResultado(ndcAgrupados, agrupamento);
				this.resultados.sort((a, b) =>
					(a.nome as string).localeCompare(b.nome as string)
				);
				this.tituloColunaAgrupamento = setAgrupamentoEspecial(agrupamento);
				this.atualizarColunasTabelaNDC();
			},
			error: () => {
				this.toastrService.error(
					'Erro ao gerar tabela de NDC, tente novamente'
				);
				this.carregando = false;
				this.inputs.setLoading(false);
			},
			complete: () => {
				this.inputs.setLoading(false);
				this.carregando = false;
			},
		});
	}

	padronizarResultado(
		ndcAgrupados: RelatorioNDCResponse[],
		agrupamento: string
	) {
		return ndcAgrupados.map(item => {
			const base = {
				nome:
					agrupamento === 'MUNICIPIO_POSTO'
						? corrigeDuplicacaoNome(item.nome)
						: item.nome,
				diasComChuva: item.diasComChuva,
				somaPrecipitacao: item.somaPrecipitacao,
				mes: item.mes,
				ano: item.ano,
				data: this.getDataFormatadaByResponse(item),
			};

			if (agrupamento === 'MUNICIPIO_POSTO') {
				return {
					...base,
					latitude: item.latitude,
					longitude: item.longitude,
					altitude: item.altitude,
					municipio: item.municipio || '-',
					microrregiao: item.microrregiao || '-',
					mesorregiao: item.mesorregiao || '-',
					regiao: item.regiao || '-',
					bacia: item.bacia || '-',
				};
			}
			return base;
		});
	}

	getDataFormatadaByResponse(agrupamento: RelatorioNDCResponse) {
		if (agrupamento.mes !== null && agrupamento.mes !== undefined) {
			if (agrupamento.mes < 10) {
				return `0${agrupamento.mes}/${agrupamento.ano}`;
			}
			return `${agrupamento.mes}/${agrupamento.ano}`;
		} else if (agrupamento.ano !== null && agrupamento.ano !== undefined) {
			return `${agrupamento.ano}`;
		}

		return '-';
	}

	atualizarColunasTabelaNDC() {
		const agrupamento = this.getDataFromForm(FormularioRelatorio.AGRUPAMENTO)
			?.value;

		this.colunasNDC = [
			{
				data: 'nome',
				title: this.tituloColunaAgrupamento,
				type: 'string',
				className:
					this.tituloColunaAgrupamento === 'Município/Posto'
						? 'text-left'
						: 'text-center',
			},
		];

		if (this.periodoAgrupamento !== 'diario') {
			this.colunasNDC.push({
				data: 'data',
				title: 'Data',
				type: 'string',
				className: 'text-center',
			});
		}

		if (agrupamento === 'MUNICIPIO_POSTO') {
			this.disabledSelectList = false;
			const todasColunasDisponiveis = [
				{
					data: 'id',
					title: 'Id',
					type: 'number',
					className: 'text-center',
				},

				{
					data: 'latitude',
					title: 'Latitude',
					type: 'number',
					className: 'text-center',
					render: (valor: string) => renderCoordenada(valor),
				},

				{
					data: 'longitude',
					title: 'Longitude',
					type: 'number',
					className: 'text-center',
					render: (valor: string) => renderCoordenada(valor),
				},

				{
					data: 'altitude',
					title: 'Altitude',
					type: 'number',
					className: 'text-center',
					render: (valor: string) => renderCoordenada(valor, 1),
				},

				{
					data: 'municipio',
					title: 'Município',
					type: 'string',
					className: 'text-center',
				},

				{
					data: 'microrregiao',
					title: 'Microrregião',
					type: 'string',
					className: 'text-center',
				},

				{
					data: 'mesorregiao',
					title: 'Mesorregião',
					type: 'string',
					className: 'text-center',
				},

				{
					data: 'regiao',
					title: 'Região Pluviométrica',
					type: 'string',
					className: 'text-center',
				},

				{
					data: 'bacia',
					title: 'Bacia/Sub-bacia',
					type: 'string',
					className: 'text-center',
				},
			];
			const colunasParaExibir = todasColunasDisponiveis.filter(coluna =>
				this.colunasSelecionadas.includes(coluna.title)
			);

			this.colunasNDC.push(...colunasParaExibir);
		} else {
			this.disabledSelectList = true;
		}

		this.colunasNDC.push(
			...[
				{
					data: 'diasComChuva',
					title: 'Dias com chuva',
					type: 'number',
					className: 'text-center',
					render: (valor: string) => (isNuloOuUndefined(valor) ? '-' : valor),
				},
				{
					data: 'somaPrecipitacao',
					title: 'Pluviometria observada (mm)',
					type: 'number',
					className: 'text-center',
					render: (valor: string) => {
						return isNuloOuUndefined(valor) ? '-' : numberToBrNumber(valor, 1);
					},
				},
			]
		);
	}
}
